import * as _ from 'lodash';
import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import { AsyncSelect, FormGroup, FormLabel } from '../../generic';
import type { DependenciesResponse } from '../../../../shared/dependenciesV2';
import type { SelectOption } from '../../../types';

type DependencyOption = SelectOption<string>;

type DependencySelectorProps = {
  selectedDependencies: DependencyOption[];
  onChange: (deps: DependencyOption[]) => void;
  parentRevisionLocator: string;
  isDisabled?: boolean;
};

/**
 * Searchable multi-select dropdown for selecting dependencies.
 * Uses AsyncSelect to load dependencies as the user types.
 */
const DependencySelector: VoidFunctionComponent<DependencySelectorProps> = ({
  selectedDependencies,
  onChange,
  parentRevisionLocator,
  isDisabled = false,
}) => {
  // Fetch dependencies with search filter
  const fetchDependencies = useCallback(
    async (searchTerm: string): Promise<DependencyOption[]> => {
      if (!parentRevisionLocator || !searchTerm) {
        return [];
      }

      try {
        const { data } = await get<DependenciesResponse>(
          `/api/v2/revisions/${nuc(parentRevisionLocator)}/dependencies`,
          false,
          {
            title: searchTerm,
            count: 20, // Limit results for performance
          },
        );

        const options = (data?.dependencies || []).map((dep) => ({
          value: dep.locator,
          label: `${dep.title} (${dep.locator})`,
        }));

        // Filter out already selected dependencies
        const selectedLocators = new Set(selectedDependencies.map((dep) => dep.value));
        return options.filter((option) => !selectedLocators.has(option.value));
      } catch {
        // Silently fail - user will see no results
        return [];
      }
    },
    [parentRevisionLocator, selectedDependencies],
  );

  // Debounced version of the fetch function
  const debouncedFetchDependencies = useMemo(
    () =>
      _.debounce(async (searchTerm: string, callback: (options: DependencyOption[]) => void) => {
        const options = await fetchDependencies(searchTerm);
        callback(options);
      }, 300),
    [fetchDependencies],
  );

  // Load options wrapper for AsyncSelect
  const loadDependencyOptions = useCallback(
    (searchTerm: string): Promise<DependencyOption[]> => {
      return new Promise((resolve) => {
        debouncedFetchDependencies(searchTerm, resolve);
      });
    },
    [debouncedFetchDependencies],
  );

  return (
    <FormGroup>
      <FormLabel label="Select Dependencies">
        <AsyncSelect<DependencyOption, true>
          isMulti
          cacheOptions
          value={selectedDependencies}
          loadOptions={loadDependencyOptions}
          onChange={onChange}
          placeholder="Search for dependencies..."
          isDisabled={isDisabled}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? `No dependencies found matching "${inputValue}"` : 'Start typing to search dependencies'}
        />
      </FormLabel>
    </FormGroup>
  );
};

export default DependencySelector;
